import type { AnyRow } from './db';

let crypto_module: typeof import('crypto-js') | null = null;
let crypto_import: Promise<typeof import('crypto-js')> | null = null;
let warned_missing_key = false;

function get_key(): string | undefined {
	return import.meta.env.VITE_ENCRYPTION_KEY;
}

function warn_key_unset_if_needed(fields: readonly string[]): void {
	// H7-3: warn once when fields are declared for encryption but no key is set
	// — otherwise the app silently stores plaintext.
	if (fields.length && !get_key() && !warned_missing_key) {
		warned_missing_key = true;
		console.warn(
			'encrypt: encrypt_fields declared but VITE_ENCRYPTION_KEY unset — fields stored plaintext'
		);
	}
}

async function get_crypto(): Promise<typeof import('crypto-js') | null> {
	if (crypto_module) return crypto_module;
	if (!crypto_import) crypto_import = import('crypto-js');
	crypto_module = await crypto_import;
	return crypto_module;
}

export async function encrypt_string(text: string): Promise<string> {
	const key = get_key();
	if (!key || !text) return text;
	const CryptoJS = await get_crypto();
	if (!CryptoJS) return text;
	return encodeURIComponent(CryptoJS.AES.encrypt(text, key).toString());
}

export async function decrypt_string(encoded: string): Promise<string> {
	const key = get_key();
	if (!key || !encoded) return encoded;
	const CryptoJS = await get_crypto();
	if (!CryptoJS) return encoded;
	return CryptoJS.AES.decrypt(decodeURIComponent(encoded), key).toString(CryptoJS.enc.Utf8);
}

function looks_encrypted(value: string): boolean {
	// A3: the old /%[0-9A-Fa-f]{2}/ heuristic misclassified plaintext that
	// merely contained a percent-escape. Encrypted values are
	// encodeURIComponent(base64) — so decode, then require a base64 shape.
	let decoded: string;
	try {
		decoded = decodeURIComponent(value);
	} catch {
		return false;
	}
	return (
		decoded.length > 0 &&
		decoded.length % 4 === 0 &&
		/^[A-Za-z0-9+/=]+$/.test(decoded)
	);
}

export async function encrypt_row<T extends AnyRow>(
	row: T,
	fields: readonly string[]
): Promise<T> {
	const key = get_key();
	if (!key || !fields.length) {
		warn_key_unset_if_needed(fields);
		return row;
	}
	const out = { ...row } as Record<string, unknown>;
	for (const field of fields) {
		const value = out[field];
		if (typeof value === 'string' && value !== '') out[field] = await encrypt_string(value);
	}
	return out as T;
}

export async function decrypt_row<T extends AnyRow>(
	row: T,
	fields: readonly string[]
): Promise<T> {
	const key = get_key();
	if (!key || !fields.length) {
		warn_key_unset_if_needed(fields);
		return row;
	}
	const out = { ...row } as Record<string, unknown>;
	for (const field of fields) {
		const value = out[field];
		if (typeof value === 'string' && value !== '' && looks_encrypted(value)) {
			try {
				const decrypted = await decrypt_string(value);
				// CryptoJS returns '' on bad ciphertext instead of throwing —
				// treat that as a failed decrypt and keep the original value.
				out[field] = decrypted || value;
			} catch (error) {
				// A3: not actually encrypted (plaintext that merely looked
				// encoded) — keep the value as-is rather than corrupting it.
				console.error('decrypt_row failed for field', field, error);
			}
		}
	}
	return out as T;
}
